import { useContext, useEffect, useState, useRef, Dispatch } from 'react';
import Createable from 'react-select/creatable';
import styled from 'styled-components';
import DeleteIcon from '../../../../../../assets/images/svg/delete-icon.svg';
import { ProductListingContext } from '../../../../functions/data_formatting_functions';
import {
  actionSetAllAttributes,
  actionSetListingData,
} from '../../../../store/create_listing_actions';
import { Label } from '../../../../../../styles/style';
import { getOptionDropDownFormat } from '../../../../functions/get_options_dropdown_format';
import { BaseAttribute } from '../../../../../../data/types/attributes';
import { BaseSelectAttribute } from '../../../../../../data/types/attributes';
import { ListingState } from '../../../../../../data/types/listings';
import { Colors } from '../../../../../../config/theme';
import { EN_AR_TRANSLATION_SPLIT_CHAR } from '../../../../constants';

type SelectAttributesProps = {
  attribute: BaseSelectAttribute;
  productType: number;
};

type SelectedValuesProps = {
  label: string;
  value: string;
};

export default function SelectAttributes({
  attribute,
  productType,
}: SelectAttributesProps) {
  const {
    state,
    dispatch,
  }: {
    state: ListingState;
    dispatch: Dispatch<any>;
  } = useContext(ProductListingContext);
  const [multiple, setMultiple] = useState(attribute.is_variable);
  const [options, setOptions] = useState(
    attribute.official_values_en.length > 0
      ? attribute.official_values_en
      : attribute.values_en,
  );
  const [selectedValues, setSelectedValues] = useState<SelectedValuesProps[]>(
    [],
  );
  const [unique, setUnique] = useState(true);
  const [firstRun, setFirstRun] = useState(true);
  const initialRender = useRef(true);

  useEffect(() => {
    if (firstRun) {
      setFirstRun(false);
      return;
    }
    setMultiple(false);
    setSelectedValues([]);
    setIsVariableForAttribute(false);
  }, [productType]);

  useEffect(() => {
    state.all_attributes?.map((attr: any) => {
      if (attr.name_en === attribute.name_en && attr.type === attribute.type) {
        setTimeout(() => {
          setOptions(
            attribute.official_values_en.length > 0
              ? attribute.official_values_en
              : attribute.values_en,
          );
          let value;
          if (attr.values_en.length > 1) {
            setSelectedValues(
              getOptionDropDownFormat(
                attr.values_en?.filter((i: string) => i !== ''),
                attr.values_ar?.filter((i: string) => i !== ''),
              ),
            );
            value = attr.values_en;
          } else {
            if (attr.values_en[0]) {
              if (attr.values_ar[0])
                value =
                  attr.values_en[0] +
                  EN_AR_TRANSLATION_SPLIT_CHAR +
                  attr.values_ar[0];
              else value = attr.values_en[0] || '';
              setSelectedValues([
                {
                  label: value,
                  value: value.split(EN_AR_TRANSLATION_SPLIT_CHAR)[0],
                },
              ]);
            } else {
              setSelectedValues([]);
            }
          }
        }, 500);
      }
    });
    setUnique(false);
  }, []);

  useEffect(() => {
    if (attribute.values_en.length === 1 && attribute.values_ar.length === 0) {
      dispatch(
        actionSetListingData({
          ...state.data,
          select_attrs: state.data.select_attrs?.map((attr: any) => {
            if (
              attr.name_en === attribute.name_en &&
              attr.type === attribute.type
            ) {
              attr.values_ar.push('');
            }
            return attr;
          }),
        }),
      );
    }
  });

  const onChange = () => {
    setMultiple(!multiple);
  };
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!multiple) {
      if (selectedValues?.length >= 1) {
        setSelectedValues([selectedValues[0]]);
      } else {
        setSelectedValues([]);
      }
    } else {
      setSelectedValues(
        getOptionDropDownFormat(options, attribute.official_values_ar),
      );
    }
  }, [multiple]);

  useEffect(() => {
    if (!unique) {
      setIsVariableForAttribute(multiple);
    }
  }, [selectedValues]);

  const adjustSelectedImages = (selectedValues: any) => {
    selectedValues?.forEach((element: any) => {
      //if elemnt doesnt exist in state.data.selectedImages, add it there.
      if (!state.data.selectedImages[attribute.name_en][element]) {
        // delete key
        delete state.data.selectedImages[attribute.name_en][element];
      }
    });
  };

  const setIsVariableForAttribute = (multiple: boolean) => {
    state.data.select_attrs?.forEach((x, i: number) => {
      if (x.name_en === attribute.name_en) {
        if (multiple) {
          selectedValues
            ?.map((x: any) => x.value)
            ?.forEach((element: any) => {
              if (
                selectedValues?.length !==
                state.data.select_attrs[i]?.values_en?.length
              ) {
                state.data.combinations_table = [];
              }
              if (
                state.data.select_attrs[i]?.values_en?.find(
                  (element1: any) => element1 === element,
                ) === undefined
              ) {
                //remove elemnt from selectedImages if it doesnt exist in values_en
                delete state.data.selectedImages[attribute.name_en][element];
                state.data.combinations_table = [];
              }
            });
          adjustSelectedImages(selectedValues?.map((x: any) => x.value));
        } else {
          state.data.combinations_table = [];
          state.data.select_attrs[i].values_images = [];
          state.data.selectedImages[attribute.name_en] = {};
        }
        state.data.select_attrs[i].is_variable = multiple;
        state.data.select_attrs[i].values_en = selectedValues?.map(
          (x: any) => x.value,
        );
        const translatedList = options
          .map((selectedValue) => {
            // Find the index of the selected value in both official lists
            const enIndex = attribute.official_values_en.indexOf(selectedValue);
            const arIndex = attribute.official_values_ar.indexOf(selectedValue);

            // Ensure both indices are valid (-1 means not found)
            if (enIndex !== -1) {
              // Use the mutual index to retrieve translations
              return attribute.official_values_ar[enIndex];
            }
            if (arIndex !== -1) {
              return attribute.official_values_en[arIndex];
            }
          })
          .filter((x) => x !== undefined);
        state.data.select_attrs[i].values_ar = translatedList as string[];

        if (state.data.select_attrs[i])
          state.data.select_attrs[i].values_en = options;

        dispatch(
          actionSetListingData({
            ...state.data,
            select_attrs: [...state.data.select_attrs],
            combinations_table: [...state.data.combinations_table],
          }),
        );
      }
    });

    state.all_attributes?.forEach((x: BaseAttribute, i: number) => {
      if (x.name_en === attribute.name_en) {
        state.all_attributes[i].is_variable = multiple;
        state.all_attributes[i].values_en = selectedValues?.map((x) => x.value);
        state.all_attributes[i].values_ar = state.all_attributes[i]
          .values_en!.map((x: string) => {
            const indexInOfficialValues = state.all_attributes[
              i
            ].official_values_en!.findIndex((y) => y === x);
            return (
              state.all_attributes[i].official_values_ar![
                indexInOfficialValues
              ] ??
              selectedValues
                ?.find((y) => y.value === x)
                ?.label.split(EN_AR_TRANSLATION_SPLIT_CHAR)[1] ??
              ''
            );
          })
          .filter((x: any) => x !== null);
        // if (state?.data?.select_attrs[i])
        //   state.data.select_attrs[i].values_en = options;
        dispatch(actionSetAllAttributes([...state.all_attributes]));
      }
    });
  };

  const setValues = (values: any) => {
    if (!values) {
      setSelectedValues?.([]);
      return;
    }

    if (Array.isArray(values)) {
      // Case: values is a list
      values = values.map((x) => {
        if (x.value && x.label.includes(EN_AR_TRANSLATION_SPLIT_CHAR)) {
          return {
            label: x.label,
            value: x.label.split(EN_AR_TRANSLATION_SPLIT_CHAR)[0],
          };
        }
        return x;
      });
    } else if (values.value) {
      // Case: values is a single element
      if (values.label.includes(EN_AR_TRANSLATION_SPLIT_CHAR)) {
        values = {
          label: values.label,
          value: values.label.split(EN_AR_TRANSLATION_SPLIT_CHAR)[0],
        };
      }
    }
    if (selectedValues?.includes(values)) {
      setSelectedValues(selectedValues?.filter((x: any) => x != values));
    } else {
      if (!Array.isArray(values)) {
        values = [values];
      }
      setSelectedValues(values);
    }
  };
  const remove = () => {
    dispatch(
      actionSetAllAttributes(
        state.all_attributes?.filter(
          (attr: BaseAttribute) => attr.name_en != attribute.name_en,
        ),
      ),
    );
    dispatch(
      actionSetListingData({
        ...state.data,
        select_attrs: state.data.select_attrs?.filter(
          (attr) =>
            attr.name_en != attribute.name_en ||
            attr.name_ar != attribute.name_ar,
        ),
      }),
    );
  };
  return (
    <Wrapper>
      <div className="between">
        <Label
          color="#00273380"
          style={{
            fontSize: '15px',
          }}
        >
          {attribute.name_en}
          {` `}
          {attribute.name_ar !== undefined ? `- ${attribute.name_ar}` : ``}
        </Label>
      </div>
      <div className="flex">
        <Createable
          options={getOptionDropDownFormat(
            options,
            attribute.official_values_ar.length > 0
              ? attribute.official_values_ar
              : attribute.values_ar,
          )}
          formatCreateLabel={(userInput) => (
            <p>
              Adding new value:{' '}
              <span
                style={{
                  color: Colors.greenMain,
                }}
              >
                {userInput}
              </span>
              <br />
              Keep in mind: you can use {EN_AR_TRANSLATION_SPLIT_CHAR} to
              seperate translations (EnglishTerm{EN_AR_TRANSLATION_SPLIT_CHAR}
              ArabicTerm), for example Red{EN_AR_TRANSLATION_SPLIT_CHAR}أحمر
            </p>
          )}
          className="react-select"
          isMulti={multiple}
          value={selectedValues}
          onChange={(newValue, actionMeta) => {
            console.log('newValue', newValue, 'actionMeta', actionMeta);
            // Check if actionMeta indicates removal
            if (actionMeta.action === 'clear') {
              // Value is being removed
              // Add your logic for value removal here
              // Create a copy of state data
              const newData = { ...state.data };
              // Check if the key exists before deleting
              if (
                newData.selectedImages &&
                newData.selectedImages[attribute.name_en]
              ) {
                // Delete the key completely
                newData.selectedImages[attribute.name_en] = {};
                // Update state with new data
                dispatch(actionSetListingData(newData));
              }
              // Update state with new value
            }
            if (
              actionMeta.action === 'remove-value' ||
              actionMeta.action === 'pop-value'
            ) {
              // Value is being removed
              const removedValue = actionMeta.removedValue;
              // Add your logic for value removal here
              // Create a copy of state data
              const newData = { ...state.data };

              // Check if the key exists before deleting
              if (
                newData.selectedImages &&
                newData.selectedImages[attribute.name_en] &&
                newData.selectedImages[attribute.name_en][removedValue.value]
              ) {
                // Delete the key completely
                delete newData.selectedImages[attribute.name_en][
                  removedValue.value
                ];

                // Update state with new data
                dispatch(actionSetListingData(newData));
              }
              // Update state with new value
            }
            setValues(newValue);
            if (Array.isArray(newValue) && newValue.length > 1) {
              const updatedSelectAttribute = state.data.select_attrs?.map(
                (attr: any) => {
                  if (attr.name_en === attribute.name_en) {
                    const englishValues = newValue.map((x: any) => x.value);
                    const arabicValues = newValue.map(
                      (x: any) =>
                        x.label.split(EN_AR_TRANSLATION_SPLIT_CHAR)[1] ?? '',
                    );
                    attr.values_en = englishValues;
                    attr.values_ar = arabicValues;
                  }
                  return attr;
                },
              );
              dispatch(
                actionSetListingData({
                  ...state.data,
                  select_attrs: updatedSelectAttribute,
                }),
              );
            }
          }}
          noOptionsMessage={() =>
            'No values found! Press enter to add this value '
          }
          defaultValue={selectedValues}
        />
        <div // <Icon
          className={`position-relative info-hover attributes-icons flex ${
            attribute.custom && productType === 1
              ? 'justify-content-between'
              : 'justify-content-end'
          }`}
        >
          {attribute.custom && (
            <img
              src={DeleteIcon}
              alt="delete_icon"
              className="cursor-pointer"
              onClick={remove}
            />
          )}
          {productType === 1 && (
            <div className="checkBoxes flex">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name={`multiple${attribute.name_en}`}
                  id={`multiple${attribute.name_en}`}
                  checked={multiple}
                  onChange={onChange}
                  onClick={onChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  .margin {
    margin-top: 10px;
  }
  .flex {
    display: flex;
    align-items: center;
  }
  .justify-content-between {
    /* display: flex; */
    justify-content: space-between;
  }
  .justify-content-end {
    /* display: flex; */
    justify-items: flex-end;
  }

  .position-relative {
    position: relative;
  }
`;
